import { initialize } from 'redux-form';
import { ActionCreators as UndoActionCreators } from 'redux-undo';
import { generatePath } from 'react-router-dom';
import type { StoryModel } from 'types/story';
import { STORY_VERSIONS } from 'common/constants';
import { rebaseStoryEdits } from 'common/story-edit-operations/story-document';
import type { AdminThunkAction, AdminThunkDispatch, AdminReducerState } from 'admin/reducers';
import { FORM_MODEL } from 'admin/constants/common';
import { STORY_EDIT_OPERATIONS } from 'admin/constants/actions';
import { STORY_FLOW_PAGE } from 'admin/constants/routes';
import { toast } from 'admin/components/common/MessageContainer/toast';
import { fetchStory } from 'admin/actions/story/get-story';
import { refreshEditedCard } from 'admin/actions/card-editor/refresh-edited-card';
import { isEditOperationsSave } from 'admin/utils/story-save-mechanism';
import { storyEditSaverFor } from 'admin/utils/story-edit-operations/saver';
import { adminHistory } from 'admin/utils/admin-history';

const isLatestOf = (state: AdminReducerState, storyId: string) =>
	state.storyEditor.story?.id === storyId && state.version.current === STORY_VERSIONS.latest;

export type CatchUpOptions = {
	/** The caught-up change is this tab's own (its chat's), so the card editor keeps it as an undo step. */
	keepUndoHistory?: boolean;
};

/** Refreshes the open card; leaves the card editor, like its Flow link, when the card no longer exists. */
async function catchUpEditedCard(
	dispatch: AdminThunkDispatch,
	getState: () => AdminReducerState,
	story: StoryModel,
	{ keepUndoHistory }: CatchUpOptions
) {
	const cardId = getState().cardEditor.present.data?._id;
	if (!cardId) return;

	const status = await dispatch(refreshEditedCard(story, cardId));
	if (status === 'missing') {
		toast.warn('This card was deleted by another editor.');
		adminHistory.push(generatePath(STORY_FLOW_PAGE, { storyId: story.id }));
		return;
	}

	// Another writer changed what undo restores: undo must not bring back values older than theirs.
	if (status === 'changed' && !keepUndoHistory) {
		dispatch(UndoActionCreators.clearHistory());
	}
}

/**
 * Replaces the editor's state with the latest story plus this tab's unacknowledged actions
 * (see Staying current in docs/specs/story-edit-operations.md).
 */
function runCatchUp(options: CatchUpOptions): AdminThunkAction {
	return async (dispatch, getState) => {
		const loadedStory = getState().storyEditor.story;
		if (!isEditOperationsSave || !loadedStory || !isLatestOf(getState(), loadedStory.id)) return;
		const storyId = loadedStory.id;
		const saver = storyEditSaverFor(storyId);

		// No request may start before the caught-up state is in place, and none may be in flight
		// during the fetch: its actions could be both in the fetched story and re-applied.
		const release = saver?.hold();
		try {
			await saver?.whenNoRequestInFlight();
			// Not stored as fetched: the editor would show it without the pending actions, and the card editor
			// would leave for "not found" if the edited card was deleted.
			const { result: fetched } = await dispatch(fetchStory({ storyId }));
			if (!fetched) throw new Error('The latest story could not be loaded.');
			// The editor may have left the story or its latest version meanwhile.
			if (!isLatestOf(getState(), storyId)) return;

			const { story } = rebaseStoryEdits(fetched, saver?.pending ?? []);
			dispatch({ type: STORY_EDIT_OPERATIONS.CAUGHT_UP, payload: { story } });
			// Keeps the values being typed in an open Story Settings modal; they are saved on submit.
			dispatch(initialize(FORM_MODEL.EDIT_STORY, story, { keepDirty: true }));
			await catchUpEditedCard(dispatch, getState, story, options);
		} finally {
			release?.();
		}
	};
}

let running: Promise<void> | undefined;
let scheduled: Promise<void> | undefined;
let scheduledOptions: CatchUpOptions = {};

/**
 * Coalesced: one catch-up runs at a time; a request during a run schedules exactly one more,
 * which keeps the undo history when any of the requests it stands for asked to.
 * With `storyId`, does nothing unless that story is the loaded one: a saver can outlive its story.
 */
export function catchUpStory(storyId?: string, options: CatchUpOptions = {}): AdminThunkAction {
	return async (dispatch, getState) => {
		if (storyId && getState().storyEditor.story?.id !== storyId) return;
		if (!running) {
			running = runCatchUp(options)(dispatch, getState).finally(() => {
				running = undefined;
			});
			await running;
			return;
		}
		scheduledOptions = { keepUndoHistory: scheduledOptions.keepUndoHistory || options.keepUndoHistory };
		scheduled ??= running
			.catch(() => undefined)
			.then(() => {
				scheduled = undefined;
				const nextOptions = scheduledOptions;
				scheduledOptions = {};
				return catchUpStory(undefined, nextOptions)(dispatch, getState);
			});
		await scheduled;
	};
}
